import { useEffect, useState } from "react";
import { BookOpen, Sparkles, Share2, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const field = "w-full text-sm rounded-xl border border-stone-300 px-3 py-2 bg-white focus:outline-none focus:border-amber-600";
const pill = (on) => `text-xs font-semibold px-3 py-1.5 rounded-full transition ${on ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`;
const when = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

function Composer({ prompt, habits, onSaved }) {
  const [text, setText] = useState("");
  const [habitId, setHabitId] = useState("");
  const [usePrompt, setUsePrompt] = useState(false);
  const save = async () => {
    try {
      const r = await api.post("/journal", { text, prompt: usePrompt ? prompt : null, habit_id: habitId || null, tags: [] });
      setText(""); setUsePrompt(false); onSaved(r.data); toast.success("Saved — private to you.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="warm-card p-5 space-y-3" data-testid="journal-composer">
      <button className="text-xs text-stone-500 hover:text-stone-800 text-left" onClick={() => setUsePrompt((p) => !p)} data-testid="journal-use-prompt">
        {usePrompt ? "Writing to today's prompt — " : "Today's prompt (tap to use): "}<span className="italic">{prompt}</span></button>
      <textarea className={field} rows={4} placeholder="Write freely. Only you can read this." value={text} onChange={(e) => setText(e.target.value)} data-testid="journal-text" />
      <div className="flex items-center gap-2 flex-wrap">
        <select className="text-xs rounded-full bg-stone-100 px-3 py-1.5" value={habitId} onChange={(e) => setHabitId(e.target.value)} data-testid="journal-habit">
          <option value="">No habit tag</option>{habits.map((h) => <option key={h.id} value={h.id}>{h.title}</option>)}
        </select>
        <button className="amber-btn text-sm ml-auto" disabled={!text.trim()} onClick={save} data-testid="journal-save">Save entry</button>
      </div>
    </div>
  );
}

function Entry({ e, connections, habits, reload }) {
  const [reflection, setReflection] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const reflect = async () => {
    setBusy(true);
    try { const r = await api.post(`/journal/${e.id}/reflect`); setReflection(r.data.reflection); setNote(r.data.note); }
    catch (err) { toast.error(err?.response?.data?.detail || "The reflection didn't come through."); }
    finally { setBusy(false); }
  };
  const guard = (fn) => async (...a) => { try { await fn(...a); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const keep = guard(async () => { await api.post(`/journal/${e.id}/reflection`, { text: reflection }); setReflection(null); reload(); toast.success("Reflection kept with the entry."); });
  const share = guard(async (c, shared) => { await api.post(`/journal/${e.id}/share`, { connection_id: c.id, shared }); reload(); toast.success(shared ? `Shared with ${c.other.handle}.` : `No longer shared with ${c.other.handle}.`); });
  const remove = guard(async () => { await api.delete(`/journal/${e.id}`); reload(); });
  const habit = habits.find((h) => h.id === e.habit_id);
  return (
    <li className="py-4 space-y-2" data-testid={`journal-entry-${e.id}`}>
      <div className="flex items-center justify-between gap-2 text-xs text-stone-500">
        <span>{when(e.created_at)}{habit ? ` · ${habit.title}` : ""}{e.shared_with?.length ? ` · shared with ${e.shared_with.length}` : " · private"}</span>
        <div className="flex gap-1">
          <button className="p-1 rounded hover:bg-stone-100" title="Reflect with AI (opt-in, de-identified)" onClick={reflect} disabled={busy} data-testid={`journal-reflect-${e.id}`}><Sparkles className="w-4 h-4" /></button>
          <button className="p-1 rounded hover:bg-stone-100" title="Share with one person" onClick={() => setSharing((s) => !s)} data-testid={`journal-share-toggle-${e.id}`}><Share2 className="w-4 h-4" /></button>
          <button className="p-1 rounded hover:bg-stone-100" title="Delete" onClick={remove} data-testid={`journal-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {e.prompt && <div className="text-xs italic text-stone-400">{e.prompt}</div>}
      <p className="text-sm text-stone-800 whitespace-pre-wrap">{e.text}</p>
      {e.reflection && <p className="text-xs text-stone-600 bg-amber-50 rounded-xl p-3" data-testid={`journal-kept-reflection-${e.id}`}>{e.reflection}</p>}
      {(reflection || note) && (
        <div className="bg-stone-50 rounded-xl p-3 text-xs space-y-2" data-testid={`journal-reflection-${e.id}`}>
          <p className="text-stone-700">{reflection || note}</p>
          {reflection && <div className="flex gap-2"><button className={pill(true)} onClick={keep} data-testid={`journal-keep-${e.id}`}>Keep it</button><button className={pill(false)} onClick={() => setReflection(null)}>Discard</button></div>}
        </div>
      )}
      {sharing && (
        <div className="flex flex-wrap gap-1.5" data-testid={`journal-share-list-${e.id}`}>
          {connections.length === 0 && <span className="text-xs text-stone-500">Connect with someone on My people first.</span>}
          {connections.map((c) => { const on = e.shared_with?.includes(c.other.id); return <button key={c.id} className={pill(on)} onClick={() => share(c, !on)} data-testid={`journal-share-${e.id}-${c.id}`}>{on ? `Shared with ${c.other.handle} · un-share` : `Share with ${c.other.handle}`}</button>; })}
        </div>
      )}
    </li>
  );
}

export default function Journal() {
  const [entries, setEntries] = useState(null);
  const [q, setQ] = useState("");
  const [prompt, setPrompt] = useState("");
  const [habits, setHabits] = useState([]);
  const [connections, setConnections] = useState([]);
  const load = (query = q) => api.get(`/journal${query ? `?q=${encodeURIComponent(query)}` : ""}`).then((r) => setEntries(r.data.entries));
  useEffect(() => {
    load(""); api.get("/journal/prompt").then((r) => setPrompt(r.data.prompt)); api.get("/habits").then((r) => setHabits(r.data || [])).catch(() => {});
    api.get("/connections").then((r) => setConnections(r.data.connections.filter((c) => c.direction === "i_share" && c.status === "active"))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="journal-page">
      <div>
        <h1 className="font-display text-4xl font-bold text-stone-900 flex items-center gap-2"><BookOpen className="w-7 h-7" /> Journal</h1>
        <p className="text-sm text-stone-600 mt-1">Private by default. Share one entry at a time if you choose; un-share any time. AI reflection is opt-in per entry and sees only that entry, with names and numbers removed.</p>
      </div>
      <Composer prompt={prompt} habits={habits} onSaved={() => load()} />
      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" /><input className={`${field} pl-9`} placeholder="Search your entries" value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} data-testid="journal-search" /></div>
      <section className="warm-card p-5">
        {entries === null ? <div className="text-sm text-stone-500">Loading…</div> : entries.length === 0 ? <div className="text-sm text-stone-500 text-center py-6" data-testid="journal-empty">Nothing here yet. The first entry can be one line.</div>
          : <ul className="divide-y divide-stone-100">{entries.map((e) => <Entry key={e.id} e={e} connections={connections} habits={habits} reload={() => load()} />)}</ul>}
      </section>
    </div>
  );
}
